import { useTranslation } from '@modernloop/i18n';
import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';

import NotificationHeaderMenu from './NotificationHeaderMenu';
import NotificationListItem from './NotificationListItem';
import type { InboxNotificationView } from './types';

type NotificationListProps = {
  notifications: InboxNotificationView[];
  loading: boolean;
  unreadCount: number;
  onNotificationClick: (notification: InboxNotificationView) => void;
  onToggleRead: (notification: InboxNotificationView) => void;
  onMuteType: (notification: InboxNotificationView) => void;
  onMarkAllRead: () => void;
  onOpenSettings: () => void;
  onOpenNotifications: () => void;
  showOpenNotifications?: boolean;
  /** Hide the internal title/actions header when the surrounding page chrome already provides one. */
  showHeader?: boolean;
  /** Scroll cap for the item list; pass 'none' for full-page layouts that should grow naturally. */
  maxListHeight?: number | 'none';
};

/** Presents notification loading, empty, and populated states without data-layer dependencies. */
const NotificationList = ({
  notifications,
  loading,
  unreadCount,
  onNotificationClick,
  onToggleRead,
  onMuteType,
  onMarkAllRead,
  onOpenSettings,
  onOpenNotifications,
  showOpenNotifications = true,
  showHeader = true,
  maxListHeight = 400,
}: NotificationListProps) => {
  const { t } = useTranslation();

  return (
    <>
      {showHeader && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
            <Typography variant={'h6' as TypographyProps['variant']} fontWeight={600}>
              {t('hiring-dashboard-nav:nav.notificationInbox.title')}
            </Typography>
            <NotificationHeaderMenu
              unreadCount={unreadCount}
              onMarkAllRead={onMarkAllRead}
              onOpenSettings={onOpenSettings}
              onOpenNotifications={onOpenNotifications}
              showOpenNotifications={showOpenNotifications}
            />
          </Stack>
          <Divider />
        </>
      )}
      {loading && notifications.length === 0 ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={20} />
        </Box>
      ) : notifications.length === 0 ? (
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            {t('hiring-dashboard-nav:nav.notificationInbox.emptyState')}
          </Typography>
        </Box>
      ) : (
        <Stack
          sx={maxListHeight === 'none' ? undefined : { overflowY: 'auto', maxHeight: maxListHeight }}
          divider={<Divider />}
        >
          {notifications.map((notification) => (
            <NotificationListItem
              key={notification.id}
              notification={notification}
              onClick={onNotificationClick}
              onToggleRead={onToggleRead}
              onMuteType={onMuteType}
            />
          ))}
        </Stack>
      )}
    </>
  );
};

export default NotificationList;
